import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SidebarService } from 'src/app/services/sidebar.service';
import { Subscription, Observable } from 'rxjs';

// Scroll positions survive a reload, so a key prefix keeps them apart from
// the rest of sessionStorage.
const SCROLL_KEY_PREFIX = 'scroll:';
// How long to keep waiting for a page's data to arrive before giving up on
// restoring its scroll position.
const SCROLL_RESTORE_TIMEOUT_MS = 5000;

@Component({
    selector: 'app-admin-layout',
    templateUrl: './admin-layout.component.html',
    styleUrls: ['./admin-layout.component.scss'],
    standalone: false
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  isSidebarOpen$: Observable<boolean> = this.sidebarService.isSidebarOpen$;

  private subscriptions = new Subscription();
  // Set while a NavigationStart has fired and its NavigationEnd has not:
  // whether the navigation in flight is the browser's back/forward.
  private pendingIsPopstate = false;
  private firstNavigation = true;
  private cancelPendingRestore: (() => void) | null = null;
  private readonly saveOnUnload = () => this.saveScroll(this.router.url);

  constructor(
    public location: Location,
    private router: Router,
    private sidebarService: SidebarService
  ) {}

  ngOnInit() {
    // The window never scrolls in this layout -- `<main>` does -- so this
    // tracks the scroll container itself rather than window.scrollY. The
    // position is saved per url when leaving a page and put back on browser
    // back/forward (and after a reload), once the page has enough content
    // to scroll that far. A fresh navigation starts at the top.
    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.cancelPendingRestore?.();
          this.saveScroll(this.router.url);
          this.pendingIsPopstate = event.navigationTrigger === 'popstate';
        } else if (event instanceof NavigationEnd) {
          const restore = this.pendingIsPopstate || this.firstNavigation;
          this.firstNavigation = false;
          this.pendingIsPopstate = false;
          const saved = restore ? this.readScroll(event.urlAfterRedirects) : null;
          if (saved) {
            this.restoreScroll(saved);
          } else {
            this.getScrollContainer()?.scrollTo(0, 0);
          }
        }
      })
    );
    window.addEventListener('beforeunload', this.saveOnUnload);
  }

  ngOnDestroy() {
    this.cancelPendingRestore?.();
    this.subscriptions.unsubscribe();
    window.removeEventListener('beforeunload', this.saveOnUnload);
  }

  // The element that actually scrolls the routed content. Picked by its
  // computed overflow rather than by what is currently scrollable, because at
  // restore time the page may not have loaded anything to scroll yet.
  private getScrollContainer(): HTMLElement | null {
    const candidates = [
      document.querySelector<HTMLElement>('#app-content > main'),
      document.querySelector<HTMLElement>('#app-content'),
    ];
    for (const el of candidates) {
      if (!el) continue;
      const overflowY = getComputedStyle(el).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return el;
    }
    return document.scrollingElement as HTMLElement | null;
  }

  private saveScroll(url: string): void {
    const container = this.getScrollContainer();
    if (!container || !url) return;
    try {
      sessionStorage.setItem(SCROLL_KEY_PREFIX + url, String(container.scrollTop));
    } catch {}
  }

  private readScroll(url: string): number | null {
    try {
      const raw = sessionStorage.getItem(SCROLL_KEY_PREFIX + url);
      const top = raw === null ? NaN : Number(raw);
      return Number.isFinite(top) && top > 0 ? top : null;
    } catch {
      return null;
    }
  }

  // Scrolls to `top` as soon as the container is tall enough. Pages fetch
  // their data after routing, so the content grows over the next moments;
  // each DOM change retries until the target is reachable, the timeout
  // passes, or the user scrolls on their own.
  private restoreScroll(top: number): void {
    const container = this.getScrollContainer();
    if (!container) return;

    const reachable = () => container.scrollHeight - container.clientHeight >= top;
    const attempt = () => {
      if (!reachable()) return false;
      container.scrollTop = top;
      return true;
    };
    if (attempt()) return;

    const observer = new MutationObserver(() => {
      if (attempt()) cancel();
    });
    const timer = setTimeout(() => cancel(), SCROLL_RESTORE_TIMEOUT_MS);
    const userEvents: (keyof HTMLElementEventMap)[] = ['wheel', 'touchstart', 'keydown'];
    const cancel = () => {
      observer.disconnect();
      clearTimeout(timer);
      userEvents.forEach((name) => container.removeEventListener(name, cancel));
      this.cancelPendingRestore = null;
    };
    userEvents.forEach((name) => container.addEventListener(name, cancel, { passive: true }));
    observer.observe(container, { childList: true, subtree: true });
    this.cancelPendingRestore = cancel;
  }

  isMaps(path) {
    let titlee = this.location.prepareExternalUrl(this.location.path());
    titlee = titlee.slice(1);
    return path !== titlee;
  }

  isMac(): boolean {
    return (
      navigator.platform.toUpperCase().includes('MAC') ||
      navigator.platform.toUpperCase().includes('IPAD')
    );
  }
}
